import { FATHOM_EVENTS } from '@/lib/fathom/fathom-events';
import {
  AI_NATIVE_SDLC_PATH,
  AI_NATIVE_SDLC_SUMMARY,
  AI_NATIVE_SDLC_TITLE,
} from '@/sections/resources/ebooks-and-whitepapers/ai-native-sdlc/publish-status';
import type { InternalEvent } from '../../events';

// Customer-only Build Caching Optimizer webinar (Trello Gb2LuDCe). Shared by AEs/CSMs via direct
// link, so it stays unlisted until the recording is added. Copy: Harriet's Claude Design
// "Kafka CI Event v3" (21 Oct 2026; host labels + Simon as co-host 27 Oct). Pardot handler id
// 7177 (Thomas).

const overviewIntro =
  'The Build Caching Optimizer is the AI agent in Develocity that hunts down your costliest cache misses, fixes them, proves every fix against real builds before it opens a pull request, and protects your project from regressing.';

const agendaItems = [
  {
    title: 'Find the misses that matter',
    description:
      'How we use cross-build data to identify the most impactful cache misses across your projects — where most CI time can be recovered.',
  },
  {
    title: 'See the root cause',
    description:
      'How Build Scan data shows exactly where and why your cache misses — the root causes a generic coding agent can’t see.',
  },
  {
    title: 'Fix it, with the evidence',
    description: 'How the Build Caching Optimizer fixes the issue and provides proof of its impact and effectiveness.',
  },
  {
    title: 'Live before-and-after demo',
    description: 'A real-world example: the cache misses gone, the build faster, every result backed by a Build Scan.',
    badge: { label: 'Live', variant: 'default' },
  },
  {
    title: 'Where it’s headed',
    description:
      'Agentic loops that keep builds across the company fast, continuously, and protect them from regressing.',
    badge: { label: 'Roadmap', variant: 'roadmap' },
  },
] as const;

export const buildCachingOptimizerWebinar: InternalEvent = {
  slug: 'build-caching-optimizer-develocity-11-26',
  title: 'Find it. Fix it. Prove it: Build Caching Optimizer in Develocity',
  description:
    'Stop wasting CI spend. See how Develocity’s AI agent finds your costliest cache misses, fixes them, and proves every fix against real builds.',
  eventType: 'Online',
  date: 'November 4, 2026',
  time: '08:00 PST',
  timeStamp: '2026-11-04T08:00:00-08:00',
  isFree: true,
  topics: ['AI', 'Acceleration'],
  pardotFormSubmitLink: 'https://go.develocity.ai/l/68052/2026-10-20/n48hqn',
  zoomId: '84886602703',
  heroImageLink: 'EVENT_buildCachingOptimizerWebinar-NOV2026.jpg',
  heroImageMobileLink: 'EVENT_buildCachingOptimizerWebinar-NOV2026-mobile.jpg',
  // The recording: paste the YouTube URL between the quotes ('https://www.youtube.com/watch?v=…').
  // While empty the page shows "Recording Coming Soon"; once set, the hero art plays it and the
  // event lists on /events as a past event (see unlistedUntilRecording).
  videoLink: '',
  unlistedUntilRecording: true,
  fullDescription: overviewIntro,
  discussionTopics: agendaItems.map(item => item.title),
  speakers: [
    {
      name: 'Troy Partain',
      label: 'Host',
      title: 'Pre-Sales Engineer',
      company: 'Develocity',
      bio: '',
      headshotLink: 'troyPartain-cutout.webp',
    },
    {
      name: 'Jan Mittelman',
      label: 'Host',
      title: 'Senior Product Manager, AI Strategy Lead',
      company: 'Develocity',
      bio: '',
      headshotLink: 'janMittelman-cutout.webp',
    },
    {
      name: 'Stefan Wolf',
      title: 'Principal Software Engineer',
      company: 'Develocity',
      bio: '',
      headshotLink: 'stefanWolf-cutout.webp',
    },
    {
      name: 'Simon Taddiken',
      label: 'Co-Host',
      title: 'Staff Software Engineer',
      company: 'Develocity',
      bio: '',
      headshotLink: 'simonTaddiken-cutout.webp',
    },
  ],
  webinarPage: {
    eyebrow: 'Webinar',
    headline: 'Find it. Fix it. Prove it.',
    lede: 'Stop wasting CI spend. See how Develocity’s AI agent finds your costliest cache misses, fixes them, and proves every fix against real builds.',
    // 4 Nov: US and Europe are both back on standard time.
    timeZones: [
      { timeZone: 'America/Los_Angeles', label: 'PT' },
      { timeZone: 'America/New_York', label: 'ET' },
      { timeZone: 'Europe/Berlin', label: 'CET' },
    ],
    registration: {
      heading: 'Save your seat',
      intro: 'Free to attend. Can’t make it live? Register and we’ll send you the recording.',
    },
    overview: {
      heading: 'Your CI is running work it already did.',
      subheading: 'Meet the Build Caching Optimizer',
      intro: overviewIntro,
      points: [
        {
          label: 'Find.',
          text: 'The Build Caching Report ranks savings opportunities by recoverable CI time across every project on your Develocity instance.',
        },
        {
          label: 'Fix.',
          text: 'The Build Caching Optimizer fixes each miss in the owning project, verifies the fix against real builds, and opens a pull request for your normal review.',
        },
        {
          label: 'Prove.',
          text: 'Re-run the report to see what changed, with every result backed by a Build Scan.',
        },
      ],
    },
    agenda: { heading: 'What you’ll see', items: agendaItems },
    requirements: {
      eyebrow: 'Requirements',
      heading: 'What you need to run it',
      intro:
        'The Build Caching Optimizer ships as skills. They install from your Develocity instance and run on your own AI model.',
      items: [
        { title: 'Develocity 2026.3 or later', description: 'Self-managed or SaaS.' },
        {
          title: 'Develocity MCP Server enabled',
          description:
            'Already using the Develocity skills released in 2026.2? Then you have the MCP Build Scan comparison the optimizer depends on.',
        },
        { title: 'Build Cache in use', description: 'On your Gradle or Maven builds.' },
      ],
    },
    speakersHeading: 'Meet your speakers',
    // The webinar promoted the AI-native SDLC whitepaper. Copy is the paper's homepage card's.
    pastPromo: {
      eyebrow: 'Whitepaper',
      heading: AI_NATIVE_SDLC_TITLE,
      intro: AI_NATIVE_SDLC_SUMMARY,
      cta: {
        label: 'Read the whitepaper',
        href: AI_NATIVE_SDLC_PATH,
        fathomEvent: FATHOM_EVENTS.eventsWebinar.pastPromo.ai_native_sdlc.click,
      },
    },
  },
};
